import React, { useEffect } from "react";
import { withAuth } from "../../hooks/useAuth";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { LuShoppingCart, LuArrowRight } from "react-icons/lu";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import Layout from "../../components/ui/Layout";
import { FaHome } from "react-icons/fa";

function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('payment_intent');

  // Trigger confetti animation on component mount
  useEffect(() => {
    const duration = 1 * 1000;
    const animationEnd = Date.now() + duration;
    
    const randomInRange = (min, max) => {
      return Math.random() * (max - min) + min;
    };
    
    const confettiAnimation = () => {
      const timeLeft = animationEnd - Date.now();
      
      if (timeLeft <= 0) {
        return;
      }
      
      const particleCount = 10 * (timeLeft / duration);
      
      // Since particles fall down, start a bit higher than random
      confetti({
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: ['#406580', '#86B7D9', '#FFFFFF'],
        shapes: ['circle', 'square'],
        gravity: 1.2,
        scalar: 2,
        disableForReducedMotion: true
      });
      
      confetti({
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: ['#406580', '#86B7D9', '#FFFFFF'],
        shapes: ['circle', 'square'],
        gravity: 1.2,
        scalar: 2,
        disableForReducedMotion: true
      });
      
      requestAnimationFrame(confettiAnimation);
    };
    
    requestAnimationFrame(confettiAnimation);
    
    return () => {
      // Clean up by stopping the animation frame
      cancelAnimationFrame(confettiAnimation);
    };
  }, []);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.2,
        duration: 0.3
      }
    }
  };
  
  const childVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };
  
  const buttonVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.05, boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)" },
    tap: { scale: 0.98 }
  };

  return (
    <Layout>
      <motion.div 
        className="flex flex-col items-center justify-center min-h-screen py-12 space-y-8 md:py-24 bg-gray-50"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div 
          className="flex flex-col items-center justify-center space-y-6 text-center px-4"
          variants={childVariants}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ 
              scale: [0, 1.2, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-full bg-green-100 opacity-70 animate-pulse"></div>
            <CircleCheckIcon className="h-20 w-20 text-green-600 relative z-10" />
          </motion.div>
          
          <motion.h1 
            className="text-3xl md:text-5xl font-bold tracking-tight text-gray-900 inria-serif-regular"
            variants={childVariants}
          >
            Pagamento Completato!
          </motion.h1>
          
          <motion.div 
            className="w-24 h-1 bg-green-500 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: 96 }}
            transition={{ delay: 0.5, duration: 0.7 }}
          ></motion.div>
          
          <motion.p 
            className="max-w-[600px] text-center text-gray-600 md:text-xl"
            variants={childVariants}
          >
            Il tuo ordine <span className="font-medium text-[#406580]">#{orderId?.substring(0, 8)}</span> è stato pagato con successo ed è ora in fase di elaborazione. Riceverai presto un'email di conferma.
          </motion.p>
        </motion.div>
        
        <motion.div 
          className="bg-white p-8 rounded-xl shadow-md border border-gray-100 max-w-lg w-full mx-4"
          variants={childVariants}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-800">Cosa succede ora?</h2>
            <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
              <LuArrowRight className="text-green-600" />
            </div>
          </div>
          
          <ol className="space-y-4">
            <li className="flex">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#406580] text-white flex items-center justify-center mr-3 font-medium">
                1
              </div>
              <div>
                <p className="text-gray-800 font-medium">Email di Conferma</p>
                <p className="text-sm text-gray-600">Riceverai un'email con tutti i dettagli del tuo ordine.</p>
              </div>
            </li>
            <li className="flex">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#406580] text-white flex items-center justify-center mr-3 font-medium">
                2
              </div>
              <div>
                <p className="text-gray-800 font-medium">Preparazione dell'Ordine</p>
                <p className="text-sm text-gray-600">Il tuo ordine verrà preparato e imballato accuratamente.</p>
              </div>
            </li>
            <li className="flex">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-[#406580] text-white flex items-center justify-center mr-3 font-medium">
                3
              </div>
              <div>
                <p className="text-gray-800 font-medium">Spedizione</p>
                <p className="text-sm text-gray-600">Riceverai un'email con il numero di tracciamento quando l'ordine sarà spedito.</p>
              </div>
            </li>
          </ol>
        </motion.div>
        
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 w-full max-w-md justify-center"
          variants={childVariants}
        >
          <motion.div
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            className="w-full sm:w-auto"
          >
            <Link
              to={`/order/${orderId}`}
              className="flex items-center justify-center py-3 px-6 rounded-lg bg-[#406580] text-white font-medium hover:bg-[#305570] transition-colors shadow-sm w-full"
            >
              <LuShoppingCart className="mr-2" />
              Dettagli Ordine
            </Link>
          </motion.div>
          
          <motion.div
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            className="w-full sm:w-auto"
          >
            <Link
              to="/profilo"
              className="flex items-center justify-center py-3 px-6 rounded-lg bg-white border border-gray-200 text-gray-700 font-medium hover:bg-gray-50 transition-colors shadow-sm w-full"
            >
              <UserIcon className="mr-2 h-4 w-4" />
              Area Personale
            </Link>
          </motion.div>
        </motion.div>
        
        <motion.div 
          variants={childVariants}
          className="text-center"
        >
          <Link to="/" className="inline-flex items-center text-[#406580] hover:underline">
            <FaHome className="mr-1" /> Torna alla Home
          </Link>
        </motion.div>
      </motion.div>
    </Layout>
  );
}

function CircleCheckIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function UserIcon(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  )
}

export default withAuth(PaymentSuccess);